import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Key, Eye, EyeSlash } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { BRAND } from "@/lib/constants";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/24589250/pexels-photo-24589250.jpeg";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back, Lamar.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      <div className="relative hidden md:block">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO})` }} />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2 text-white tracking-[0.2em] text-sm">
            <Key size={20} weight="duotone" className="text-brand" /> {BRAND.mark}
          </Link>
          <div>
            <h2 className="text-4xl text-white leading-tight">Your lead engine,<br />always working.</h2>
            <p className="text-white/60 font-light mt-4 max-w-sm">
              Sign in to view fresh, AI-scored buyer and seller leads — ranked and ready to close.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-sm"
        >
          <div className="md:hidden flex items-center gap-2 text-white tracking-[0.2em] text-sm mb-10">
            <Key size={20} weight="duotone" className="text-brand" /> {BRAND.mark}
          </div>
          <p className="text-brand text-xs uppercase tracking-[0.3em] mb-3">Agent Portal</p>
          <h1 className="text-4xl text-white mb-10">Sign in</h1>

          <form onSubmit={submit} className="space-y-7" data-testid="login-form">
            <div>
              <label className="lux-label" htmlFor="email">Email</label>
              <input
                id="email"
                data-testid="login-email"
                type="email"
                className="lux-input"
                placeholder="lamar@whyterealty.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="lux-label" htmlFor="password">Password</label>
              <div className="relative">
                <input
                  id="password"
                  data-testid="login-password"
                  type={show ? "text" : "password"}
                  className="lux-input pr-10"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-0 top-3 text-white/40 hover:text-white"
                  data-testid="login-toggle-password"
                >
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="w-full bg-brand text-black py-4 rounded-sm text-sm uppercase tracking-[0.1em] font-medium hover:bg-brand-hover transition-colors disabled:opacity-60"
            >
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>

          <Link to="/" className="block mt-8 text-sm text-white/40 hover:text-white transition-colors">
            ← Back to website
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
